/* Projects list — create / open / delete. No top bar: the app shell
   provides the chrome; this keeps only the H1 + create form + grid
   inside the Architect scope. */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, Building2, Check, ChevronDown, Circle, Clock3, Folder, FolderInput, FolderOpen, FolderPlus, History, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import {
  architectApi,
  approvalsApi,
  ApprovalStatus,
  ApprovalStep,
  ApprovalTargetKind,
  DocKind,
  Pipeline,
  ProjectListItem,
  projectCodes,
  ApprovalsOverview,
} from './api';
import { fmtDate } from './utils';
import { toast } from './toast';
import ApproversDetail from './ApproversDetail';
import { useAuth } from '../auth/AuthContext';
import './projects-ui.css';

interface Props {
  onOpen: (id: string) => void;
  /** Offered on the cards of projects without a client — opens the link dialog. */
  onLink?: (project: ProjectListItem) => void;
  /** A linked project's card shows its client as a chip; this opens that client. */
  onOpenClient?: (clientId: string) => void;
  /** Resolves a chip's label when the list row carries only the client id. */
  clients?: { id: string; name: string; code: string }[];
  embedded?: boolean;
  /** The host already displays Projects in its persistent top header. */
  headerHasTitle?: boolean;
  onChanged?: () => void;
}

/** The backend adds `has_active_run` to each project when an architect run
    is in progress (WB3). Drives the card's «Generating…» chip + pulse. */
function isGenerating(p: ProjectListItem): boolean {
  return Boolean(p.has_active_run);
}

/** True while ANY module run is live for the project (DESIGN12 §F1) — the
    architect run (has_active_run) OR an estimate/proposal run surfaced by the
    pipeline `running` flags. Used only to decide whether to keep polling so
    the animated pipeline dots stay live and self-clear; the flags are optional
    (older backend) so a missing value reads as not-running. */
function isAnyRunning(p: ProjectListItem): boolean {
  const r = p.pipeline?.running;
  return (
    Boolean(p.has_active_run) ||
    Boolean(r && (r.architecture || r.estimate || r.proposal))
  );
}

/* ═══════════ PIPELINE STRIP (DESIGN11 §P3, DESIGN23 §C2) ═════════
   The compact four-stage progress strip rendered on every projects-list
   card AND under the editor header. Each stage is a connected dot+label:
   check = done (approved / priced), filled dot = in progress (draft-with-spec /
   in review), outlined dot = not started. Statuses only — never content — so it is
   safe for every architect-surface role. Exported so EditorView reuses the
   exact same component (single source of the stage vocabulary).

   DESIGN23 §C2 — the ORDER is now Architecture · Estimate · Pricing ·
   Proposal: the proposal is approved with its final numbers, so pricing
   comes first. Only the strip's order changes; every stage keeps its own
   meaning and its own pipeline field, and proposal GENERATION gating is
   untouched (the document must exist before it can be priced). */

type StepState = 'done' | 'progress' | 'todo';

const STEP_GLYPH: Record<StepState, React.ReactNode> = {
  done: <Check size={10} strokeWidth={2.5} />,
  progress: <Circle size={5} fill="currentColor" strokeWidth={0} />,
  todo: <Circle size={5} strokeWidth={1.8} />,
};

function archStepState(s: Pipeline['architecture'] | undefined): StepState {
  if (s === 'approved') return 'done';
  if (s === 'draft') return 'progress';
  return 'todo';
}
function reviewStepState(s: 'none' | 'review' | 'approved' | undefined): StepState {
  if (s === 'approved') return 'done';
  if (s === 'review') return 'progress';
  return 'todo';
}
function pricingStepState(p?: Pipeline): StepState {
  if (p?.pricing === 'approved') return 'done';
  if (p?.pricing === 'draft') return 'progress';
  return p?.pricing === undefined && p?.priced ? 'done' : 'todo';
}

/** Per-stage tooltip copy (English — architect-surface house style).
    `p` is the whole pipeline because one stage's tip depends on another's:
    Pricing cannot be opened until a proposal document exists, and a tooltip
    that only says «Not priced» leaves root looking for a button that is not
    there (DESIGN27). */
function stepTip(key: string, state: StepState, p?: Pipeline): string {
  if (key === 'architecture') {
    return state === 'done'
      ? 'Architecture approved'
      : state === 'progress'
        ? 'Architecture drafted — awaiting approval'
        : 'Architecture not started';
  }
  if (key === 'estimate') {
    return state === 'done'
      ? 'Estimate approved'
      : state === 'progress'
        ? 'Estimate in review'
        : 'No estimate yet';
  }
  if (key === 'proposal') {
    return state === 'done'
      ? 'Proposal approved'
      : state === 'progress'
        ? 'Proposal in review'
        : 'No proposal yet';
  }
  if (state === 'done') return 'Pricing approved';
  if (state === 'progress') return 'Pricing draft — awaiting approval';
  const hasEstimate = !!p && p.estimate === 'approved';
  return hasEstimate
    ? 'Prepare pricing and approve it to start the proposal'
    : 'Approve the estimate before preparing pricing';
}

export function PipelineStrip({
  pipeline,
  className,
}: {
  pipeline?: Pipeline | null;
  className?: string;
}) {
  const p = pipeline || undefined;
  // DESIGN12 §F1 — per-module liveness. A stage whose `running` flag is true
  // gets an animated in-progress dot that beats the static amber ●. The flag
  // is optional (older backend); a missing/false value reads as not-running.
  // 'priced' has no run of its own, so it never shows the live state.
  const running = p?.running;
  const steps: Array<{
    key: string;
    label: string;
    state: StepState;
    running: boolean;
  }> = [
    {
      key: 'architecture',
      label: 'Architecture',
      state: archStepState(p?.architecture),
      running: !!running?.architecture,
    },
    {
      key: 'estimate',
      label: 'Estimate',
      state: reviewStepState(p?.estimate),
      running: !!running?.estimate,
    },
    // DESIGN23 §C2 — Pricing now sits BEFORE Proposal: a proposal is approved
    // with its final numbers, so the prices are the step that comes first.
    {
      key: 'priced',
      label: 'Pricing',
      state: pricingStepState(p),
      running: false,
    },
    {
      key: 'proposal',
      label: 'Proposal',
      state: reviewStepState(p?.proposal),
      running: !!running?.proposal,
    },
  ];
  return (
    <div
      className={'arch-pipeline' + (className ? ' ' + className : '')}
      role="list"
      aria-label="Pipeline stages"
    >
      {/* The connector travels INSIDE the unit of the step it leads to, so a
          wrap can never strand a dangling line-stub at the end of a row. */}
      {steps.map((s, i) => (
        <span className="arch-pipe-unit" key={s.key}>
          {i > 0 ? (
            <span
              className={'arch-pipe-conn ' + steps[i - 1].state}
              aria-hidden="true"
            />
          ) : null}
          <span
            className={
              'arch-pipe-step ' + s.state + (s.running ? ' running' : '')
            }
            role="listitem"
            title={s.running ? 'Generating right now…' : stepTip(s.key, s.state, p)}
          >
            <span className="arch-pipe-dot" aria-hidden="true">
              {s.running ? (
                <span className="arch-spinner arch-pipe-spinner" />
              ) : (
                STEP_GLYPH[s.state]
              )}
            </span>
            <span className="arch-pipe-label">{s.label}</span>
          </span>
        </span>
      ))}
    </div>
  );
}

/* ═══════════ APPROVAL PROGRESS (DESIGN23 §C3) ═══════════════════
   The shared «Approved 1 of 2» affordance every approve control now carries —
   architecture (editor bar), estimate rows, proposal rows. It lives here for
   the same reason PipelineStrip / InlineRename / ProjectCode do: this module
   is the architect surface's home for atoms shared across the views, so all
   three panels render byte-identical copy from ONE source.

   THE RULE THIS UI MUST NOT BREAK: with no policy configured the screen looks
   EXACTLY as it does today. That means:
     • a failed / missing status is NEVER a hidden button — canApproveNow()
       answers `true` when nothing loaded;
     • the counter is only drawn once it says something (a multi-approval
       policy, or votes already cast) — «Approved 0 of 1» is never printed;
     • the muted «Waiting for the assigned approvers» line is only rendered by
       a caller that is still AWAITING approval (never under an approved doc).

   Privacy: the TIME of an approval is shown to everyone; the NAME is whatever
   the server chose to send (the estimate step keeps its root-only identity
   rule and arrives as null for everyone else). */

/** The tooltip listing every vote — «Name · 30/07, 14:12» per line, or the
    time alone when the viewer may not see who approved. */
export function approvalTooltip(status: ApprovalStatus): string {
  const rows = status.approvals || [];
  if (!rows.length) return 'No approvals yet';
  return rows
    .map((a) => {
      const when = fmtDate(a.approved_at);
      const who = a.user_name || 'Approved';
      const actor = a.actor_name && a.actor_user_id !== a.user_id ? ' · by ' + a.actor_name + ' (Root)' : '';
      return (when ? who + ' · ' + when : who) + actor;
    })
    .join('\n');
}

/** May this viewer press approve right now? An absent status (no policy, an
    older backend, a transient failure) reads as YES — today's behaviour. */
export function canApproveNow(status?: ApprovalStatus | null): boolean {
  return status ? status.me.can_approve : true;
}

/** This viewer's own approval time, or null — drives the quiet
    «You approved · <time>» state that replaces the button. */
export function myApprovalTime(
  status?: ApprovalStatus | null,
  userId?: string | null,
): string | null {
  if (!status || !userId) return null;
  if (!status.me.has_approved) return null;
  const row = (status.approvals || []).find((a) => a.user_id === userId);
  return row ? row.approved_at : null;
}

export function ApprovalNote({
  status,
  userId,
  pending,
  className,
}: {
  status?: ApprovalStatus | null;
  userId?: string | null;
  /** Is this step still AWAITING approval? Only then may the note say who it
      is waiting for — an approved document must never read as pending. */
  pending: boolean;
  className?: string;
}) {
  if (!status) return null;
  /* The counter is drawn only when it SAYS something:
       • while a step is still pending, a multi-approval policy is worth
         showing from «0 of 2» — that IS the news;
       • once a step is settled, only real votes are shown. A document
         approved BEFORE the policy existed carries no votes, and printing
         «Approved 0 of 2» under its approved chip would read as if the
         approval had been taken away. It never is. */
  const showMeter = status.given > 0 || (pending && status.required > 1);
  const mine = pending ? myApprovalTime(status, userId) : null;
  const waiting = pending && !status.me.can_approve;
  if (!showMeter && !mine && !waiting) return null;

  const names = (status.approvers || [])
    .map((u) => u.display_name || u.username)
    .filter(Boolean);

  return (
    <div className={'arch-appr' + (className ? ' ' + className : '')}>
      {showMeter ? (
        <span
          className={
            'arch-appr-meter' + (status.given >= status.required ? ' met' : '')
          }
          title={approvalTooltip(status)}
        >
          Approved {status.given} of {status.required}
        </span>
      ) : null}
      {mine ? (
        <span className="arch-appr-mine" title={approvalTooltip(status)}>
          You approved{fmtDate(mine) ? ' · ' + fmtDate(mine) : ''}
        </span>
      ) : null}
      {waiting ? (
        <span className="arch-appr-wait">
          {status.turn && status.turn.id !== userId && (status.approvers || []).some((u) => u.id === userId)
            ? 'Waiting for ' + status.turn.name + ' to approve first'
            : 'Waiting for the assigned approvers' +
              (names.length ? ' — ' + names.join(', ') : '')}
        </span>
      ) : null}
    </div>
  );
}

/** Live approval status for ONE target. Silent on failure by design: no
    status means «no policy», which is exactly today's behaviour, and a
    background read must never toast. `reload` is called after an approve and
    from the surrounding poll so a colleague's vote shows up on its own. */
export function useApprovalStatus(
  step: ApprovalStep,
  targetKind: ApprovalTargetKind,
  targetId: string | null | undefined,
  enabled: boolean,
): { status: ApprovalStatus | null; reload: () => void } {
  const [status, setStatus] = useState<ApprovalStatus | null>(null);
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((n) => n + 1), []);
  useEffect(() => {
    if (!enabled || !targetId) {
      setStatus(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const st = await approvalsApi.getStatus({
          step,
          target_kind: targetKind,
          target_id: targetId,
        });
        if (!cancelled) setStatus(st);
      } catch {
        /* keep whatever we had — never a toast, never a hidden button */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [step, targetKind, targetId, enabled, tick]);
  return { status, reload };
}

/** The same, for a LIST of targets (the estimate / proposal rows). Reads are
    issued in parallel and each one fails independently — a single bad row can
    never blank the others. `ids` is joined into the effect key so a stable
    list doesn't re-fetch on every render. */
export function useApprovalStatuses(
  step: ApprovalStep,
  targetKind: ApprovalTargetKind,
  ids: string[],
  enabled: boolean,
  refreshKey: number,
): Record<string, ApprovalStatus> {
  const [map, setMap] = useState<Record<string, ApprovalStatus>>({});
  const key = ids.join(',');
  useEffect(() => {
    if (!enabled || !key) {
      setMap({});
      return undefined;
    }
    let cancelled = false;
    let inFlight = false;
    const refresh = async () => {
      if (inFlight) return;
      inFlight = true;
      const list = key.split(',');
      const out: Record<string, ApprovalStatus> = {};
      await Promise.all(
        list.map(async (id) => {
          try {
            out[id] = await approvalsApi.getStatus({
              step,
              target_kind: targetKind,
              target_id: id,
            });
          } catch {
            /* this row simply has no status → today's behaviour */
          }
        }),
      );
      if (!cancelled) setMap(out);
      inFlight = false;
    };
    void refresh();
    const timer = window.setInterval(() => { if (document.visibilityState === 'visible') void refresh(); }, 20000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [step, targetKind, key, enabled, refreshKey]);
  return map;
}

/* ═══════════ INLINE RENAME (DESIGN18 §F1) ═══════════════════════
   Click-to-rename affordance shared by the projects card AND the editor
   header (EditorView imports this — one source of the rename UX). A pencil
   appears on hover (always visible on touch); clicking it swaps the title
   for a seamless inline input that autofocuses + selects all. Enter saves,
   Esc cancels, blur saves-if-changed. While the PATCH is in flight the field
   shimmers; on success the component itself toasts «Project renamed». The
   title keeps its own class so the font metrics are identical to the static
   label — the input swaps in with no layout jump. */

/** Feather-style pencil, sized to sit inline next to a title. */
function PencilIcon() {
  return <Pencil size={13} strokeWidth={1.8} aria-hidden="true" />;
}

export function InlineRename({
  value,
  onSubmit,
  variant,
  pencilLabel = 'Rename project',
  successMessage = 'Project renamed',
  maxLength = 200,
  onEditingChange,
}: {
  value: string;
  /** Performs the PATCH (+ any refresh). Resolves on success, rejects with an
      Error whose message is already white-label English on failure. */
  onSubmit: (name: string) => Promise<void>;
  variant: 'card' | 'header';
  pencilLabel?: string;
  successMessage?: string;
  maxLength?: number;
  /** Lets a container (the projects card) suppress its click-through while a
      rename is open — the card must never open the project mid-edit. */
  onEditingChange?: (editing: boolean) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    onEditingChange?.(editing);
    // onEditingChange is an inline parent callback; edit-state is the trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  // Track an external name change (e.g. the 5s list poll) while idle so the
  // field always opens on the freshest value.
  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  // Autofocus + select-all the instant the field opens.
  useEffect(() => {
    if (!editing) return;
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    el.select();
  }, [editing]);

  const begin = useCallback(
    (e?: React.SyntheticEvent) => {
      if (e) e.stopPropagation();
      if (saving) return;
      setDraft(value);
      setEditing(true);
    },
    [saving, value],
  );

  const cancel = useCallback(() => {
    setEditing(false);
    setDraft(value);
  }, [value]);

  const commit = useCallback(async () => {
    if (saving) return;
    const next = draft.trim();
    // Empty or unchanged → close with no request (an empty name is invalid;
    // an unchanged one is a no-op — never fire the cascade for either).
    if (!next || next === value.trim()) {
      cancel();
      return;
    }
    setSaving(true);
    try {
      await onSubmit(next);
      setEditing(false);
      toast(successMessage, 'ok');
    } catch (err: any) {
      toast(err.message || 'Rename failed', 'err');
      // Keep the field open so the operator can correct it or press Esc.
      requestAnimationFrame(() => inputRef.current?.focus());
    } finally {
      setSaving(false);
    }
  }, [saving, draft, value, onSubmit, cancel, successMessage]);

  // Clicks/mousedowns inside the field must not bubble to the card's open
  // handler (harmless in the header, load-bearing on the card).
  const stop = useCallback((e: React.SyntheticEvent) => e.stopPropagation(), []);

  if (editing) {
    return (
      <span
        className={'arch-rename arch-rename-editing ' + variant}
        onClick={stop}
        onMouseDown={stop}
      >
        <input
          ref={inputRef}
          type="text"
          className={'arch-rename-input ' + variant + (saving ? ' saving' : '')}
          value={draft}
          maxLength={maxLength}
          disabled={saving}
          aria-label={pencilLabel}
          aria-busy={saving}
          onChange={(e) => setDraft(e.target.value)}
          onClick={stop}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void commit();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              cancel();
            }
          }}
          onBlur={() => {
            if (!saving) void commit();
          }}
        />
      </span>
    );
  }

  const titleClass =
    variant === 'header' ? 'arch-editor-title' : 'arch-project-card-name';
  return (
    <span className={'arch-rename ' + variant}>
      <span
        className={titleClass}
        // In the header the whole name is click-to-rename; on the card a plain
        // click still opens the project, so only the pencil starts a rename.
        onClick={variant === 'header' ? begin : undefined}
      >
        {value}
      </span>
      <button
        type="button"
        className="arch-rename-pencil"
        aria-label={pencilLabel}
        title={pencilLabel}
        onClick={begin}
      >
        <PencilIcon />
      </button>
    </span>
  );
}

/* ═══════════ PROJECT CODE (DESIGN20 §F1) ════════════════════════
   The document-code line shared by the projects card AND the editor header
   (EditorView imports this — one source of the code UX, like InlineRename).
   It renders the computed chips — a mono `W26078` / `TS26078` (or a muted
   «No code» when the project has no number), plus a muted read-only `LP26078`
   once a proposal exists — and, on the doc chip, a click-to-edit anchored
   popover: a 5-digit number input, a Workflow / Tech-scope segmented toggle,
   a live preview and Save / Cancel. The number and doc_kind ride the ONE
   PATCH /projects/:id (patchProject); a 409 (code_taken) is shown inline so
   the operator can pick another number without losing the popover. Esc /
   Cancel / an outside click close it; the chip never moves (the popover is
   absolutely anchored), so nothing reflows. */

/** Render the fixed catcher + popover as siblings of the chip. The catcher is
    a viewport-covering transparent layer BELOW the popover: an outside click
    lands on it (closing the popover) instead of on the card, and every click
    inside this subtree is stopped from bubbling to the card's open handler. */
export function ProjectCode({
  number,
  docKind,
  pipeline,
  onSave,
  variant,
}: {
  number?: number | null;
  docKind?: DocKind | null;
  pipeline?: Pipeline | null;
  /** Performs the PATCH (+ any local refresh). Resolves on success; rejects
      with an Error whose message is already white-label English (a 409 carries
      the `code_taken` copy) on failure. */
  onSave: (patch: { number: number | null; doc_kind: DocKind }) => Promise<void>;
  variant: 'card' | 'header';
}) {
  const [open, setOpen] = useState(false);
  const [numStr, setNumStr] = useState('');
  const [kind, setKind] = useState<DocKind>('workflow');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const codes = projectCodes(number ?? null, docKind ?? 'workflow', pipeline);

  // Seed the draft from the live values every time the popover opens, so it
  // always reflects the freshest number/kind (e.g. after a background poll).
  const begin = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setNumStr(number != null ? String(number) : '');
      setKind(docKind === 'tech_scope' ? 'tech_scope' : 'workflow');
      setError(null);
      setOpen(true);
    },
    [number, docKind],
  );

  const close = useCallback(() => {
    setOpen(false);
    setError(null);
  }, []);

  // Autofocus + select the number field the instant the popover opens.
  useEffect(() => {
    if (!open) return;
    const el = inputRef.current;
    if (el) {
      el.focus();
      el.select();
    }
  }, [open]);

  const submit = useCallback(async () => {
    if (saving) return;
    const raw = numStr.trim();
    let nextNum: number | null;
    if (raw === '') {
      nextNum = null; // empty clears the code
    } else {
      const n = parseInt(raw, 10);
      if (!/^\d{4,5}$/.test(raw) || n < 1000 || n > 99999) {
        setError('Enter a number between 1000 and 99999, or clear it.');
        requestAnimationFrame(() => inputRef.current?.focus());
        return;
      }
      nextNum = n;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave({ number: nextNum, doc_kind: kind });
      setOpen(false);
      toast('Project code updated', 'ok');
    } catch (err: any) {
      // 409 code_taken (and any other failure) stays inline so the operator can
      // pick another number without the popover closing under them.
      setError(err?.message || 'Could not save the project code.');
      requestAnimationFrame(() => inputRef.current?.focus());
    } finally {
      setSaving(false);
    }
  }, [saving, numStr, kind, onSave]);

  const stop = useCallback((e: React.SyntheticEvent) => e.stopPropagation(), []);

  // Live preview of what will be saved (draft number + kind), so the toggle's
  // effect on the W…/TS… prefix is visible before committing.
  const draftTrim = numStr.trim();
  const draftDoc = draftTrim ? (kind === 'tech_scope' ? 'TS' : 'W') + draftTrim : null;
  const draftProposal =
    draftTrim && pipeline && pipeline.proposal !== 'none' ? 'LP' + draftTrim : null;

  return (
    <span
      className={'arch-code ' + variant}
      onClick={stop}
      onMouseDown={stop}
    >
      <button
        type="button"
        className={'arch-code-chip arch-code-doc' + (codes.docCode ? '' : ' empty')}
        onClick={begin}
        aria-haspopup="dialog"
        aria-expanded={open}
        title="Edit project number"
      >
        <span className="arch-code-text">{codes.docCode || 'No code'}</span>
        <span className="arch-code-pencil" aria-hidden="true">
          <PencilIcon />
        </span>
      </button>
      {codes.proposalCode ? (
        <span className="arch-code-chip arch-code-lp" title="Proposal document code">
          {codes.proposalCode}
        </span>
      ) : null}

      {open ? (
        <>
          <div
            className="arch-code-catch"
            onMouseDown={(e) => {
              e.stopPropagation();
              close();
            }}
            onClick={stop}
          />
          <div
            className={'arch-code-pop ' + variant}
            role="dialog"
            aria-label="Project number"
            onMouseDown={stop}
            onClick={stop}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                close();
              }
            }}
          >
            <label className="arch-code-pop-label" htmlFor="arch-code-num">
              Project number
            </label>
            <input
              id="arch-code-num"
              ref={inputRef}
              type="text"
              inputMode="numeric"
              className="arch-code-pop-input"
              value={numStr}
              maxLength={5}
              placeholder="26078"
              disabled={saving}
              aria-label="Project number"
              onChange={(e) =>
                setNumStr(e.target.value.replace(/[^\d]/g, '').slice(0, 5))
              }
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void submit();
                }
              }}
            />
            <div className="arch-code-seg" role="group" aria-label="Document kind">
              <button
                type="button"
                className={'arch-code-seg-btn' + (kind === 'workflow' ? ' on' : '')}
                disabled={saving}
                onClick={() => setKind('workflow')}
              >
                Workflow
              </button>
              <button
                type="button"
                className={'arch-code-seg-btn' + (kind === 'tech_scope' ? ' on' : '')}
                disabled={saving}
                onClick={() => setKind('tech_scope')}
              >
                Tech scope
              </button>
            </div>
            <div className="arch-code-pop-preview" aria-hidden="true">
              {draftDoc ? (
                <>
                  <span className="arch-code-chip arch-code-doc">{draftDoc}</span>
                  {draftProposal ? (
                    <span className="arch-code-chip arch-code-lp">{draftProposal}</span>
                  ) : null}
                </>
              ) : (
                <span className="arch-code-pop-hint">Empty clears the code.</span>
              )}
            </div>
            {error ? <div className="arch-code-pop-err">{error}</div> : null}
            <div className="arch-code-pop-actions">
              <button
                type="button"
                className="arch-btn arch-btn-primary arch-btn-sm"
                disabled={saving}
                onClick={() => void submit()}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                className="arch-btn arch-btn-ghost arch-btn-sm"
                disabled={saving}
                onClick={close}
              >
                Cancel
              </button>
            </div>
          </div>
        </>
      ) : null}
    </span>
  );
}

/* DESIGN49 — which cards show their full approval trail. Remembered per
   browser so a card root opened stays open on the next visit. */
const EXPANDED_KEY = 'arch.projects.expanded.v1';
const MASONRY_MIN = 320;
const MASONRY_GAP = 18;
function readExpanded(): Set<string> {
  try {
    const raw = localStorage.getItem(EXPANDED_KEY);
    const arr = raw ? (JSON.parse(raw) as unknown) : [];
    return new Set(Array.isArray(arr) ? arr.filter((x) => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
}

export default function ProjectsView({ onOpen, onLink, onOpenClient, clients, embedded = false, headerHasTitle = false, onChanged }: Props) {
  const { user } = useAuth();
  const isRoot = user?.isRoot === true;
  const overviewAllowed = useRef(isRoot);
  overviewAllowed.current = isRoot;
  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  // First load → skeletons instead of an empty grid that then pops.
  const [loaded, setLoaded] = useState(false);
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(readExpanded);
  const toggleExpanded = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      try { localStorage.setItem(EXPANDED_KEY, JSON.stringify(Array.from(next))); } catch { /* fine */ }
      return next;
    });
  }, []);
  // Only root requests the board with named approval trails. Other roles
  // retain the project directory without making a forbidden overview call.
  const [overview, setOverview] = useState<ApprovalsOverview | null>(null);
  const loadOverview = useCallback(async () => {
    if (!isRoot) { setOverview(null); return; }
    try {
      const data = await architectApi.getApprovalsOverview();
      if (overviewAllowed.current) setOverview(data);
    } catch {
      setOverview(null);
    }
  }, [isRoot]);
  // The card whose title is being renamed right now — while set, that card's
  // click-through to open the project is suppressed (DESIGN18 §F1.2).
  const [editingId, setEditingId] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const createMenuRef = useRef<HTMLDetailsElement>(null);

  useEffect(() => {
    if (!embedded) return undefined;
    const dismissCreateMenu = (event: PointerEvent) => {
      const menu = createMenuRef.current;
      if (menu?.open && event.target instanceof Node && !menu.contains(event.target)) menu.open = false;
    };
    document.addEventListener('pointerdown', dismissCreateMenu);
    return () => document.removeEventListener('pointerdown', dismissCreateMenu);
  }, [embedded]);

  // A quiet reload used by the create/delete handlers and by the 5s poll —
  // never surfaces a toast on failure so background polling stays silent.
  const load = useCallback(async (quiet = false) => {
    try {
      const r = await architectApi.listProjects();
      setProjects(r.projects || []);
    } catch (err: any) {
      if (!quiet) toast(err.message, 'err');
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await architectApi.listProjects();
        if (!cancelled) setProjects(r.projects || []);
      } catch (err: any) {
        if (!cancelled) toast(err.message, 'err');
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    void loadOverview();
    return () => {
      cancelled = true;
    };
  }, [loadOverview]);

  /* ── while any project has a live run (architect OR estimate/proposal),
        poll the list every 5s so the 'Generating…' chip and the animated
        pipeline dots clear themselves when the run finishes. The effect
        re-subscribes whenever that condition flips, and the interval is
        torn down on unmount / view change. ──────────────────────────── */
  const anyRunning = projects.some(isAnyRunning);
  useEffect(() => {
    if (!anyRunning) return undefined;
    const t = setInterval(() => {
      void load(true);
      // The detail moves with the runs (AI writing → awaiting X) — keep the
      // two in step while anything is live.
      void loadOverview();
    }, 5000);
    return () => clearInterval(t);
  }, [anyRunning, load, loadOverview]);

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    const input = nameRef.current;
    if (!input) return;
    const name = input.value.trim();
    if (!name) {
      toast('Enter a project name', 'err');
      return;
    }
    try {
      const r = await architectApi.createProject(name);
      input.value = '';
      toast('Project created', 'ok');
      onChanged?.();
      onOpen(r.project.id);
    } catch (err: any) {
      toast(err.message, 'err');
    }
  };

  /* Rename a project from its card (DESIGN18 §F1.2). One PATCH runs the
     server-side cascade; on success reflect the new name (and any refreshed
     pipeline) in the row at once, and surface any per-document cascade
     warning so the operator knows a doc needs a second look. Errors propagate
     to InlineRename, which keeps the field open and toasts the mapped copy. */
  const renameCard = async (p: ProjectListItem, newName: string) => {
    const r = await architectApi.renameProject(p.id, newName);
    setProjects((prev) =>
      prev.map((x) =>
        x.id === p.id
          ? {
              ...x,
              name: r.project?.name ?? newName,
              ...(r.pipeline ? { pipeline: r.pipeline } : {}),
            }
          : x,
      ),
    );
    (r.warnings || []).forEach((w) => toast(w, 'err'));
    onChanged?.();
  };

  /* Save a project's number / doc_kind from its card (DESIGN20 §F1.2). Codes
     are computed, so this is pure metadata — no cascade, no approval touch.
     Reflect the fresh number/kind (+ any pipeline) in the row at once; a 409
     (code_taken) propagates to the popover, which shows it inline and stays
     open. */
  const saveCardCode = async (
    p: ProjectListItem,
    patch: { number: number | null; doc_kind: DocKind },
  ) => {
    const r = await architectApi.patchProject(p.id, patch);
    const proj = r.project as ProjectListItem | undefined;
    setProjects((prev) =>
      prev.map((x) =>
        x.id === p.id
          ? {
              ...x,
              number: proj?.number ?? patch.number,
              doc_kind: proj?.doc_kind ?? patch.doc_kind,
              ...(r.pipeline ? { pipeline: r.pipeline } : {}),
            }
          : x,
      ),
    );
    (r.warnings || []).forEach((w) => toast(w, 'err'));
    onChanged?.();
  };

  const del = async (p: ProjectListItem, e: React.MouseEvent) => {
    e.stopPropagation();
    if (
      !window.confirm(
        'Delete project "' + p.name + '" with all its files and versions?',
      )
    )
      return;
    try {
      await architectApi.deleteProject(p.id);
      toast('Project deleted', 'ok');
      onChanged?.();
      load();
    } catch (err: any) {
      toast(err.message, 'err');
    }
  };

  /* DESIGN50 — masonry: a card that unfolds its approval trail grows DOWN
     in its own column and never stretches the cards beside it. Columns are
     counted from the container's width (the same 320px / 18px rhythm the
     grid used) and cards are dealt left→right, row by row, so the reading
     order stays exactly the list order. */
  const masonryRef = useRef<HTMLDivElement>(null);
  const [cols, setCols] = useState(3);
  useEffect(() => {
    const el = masonryRef.current;
    if (!el) return undefined;
    const measure = () => {
      const w = el.clientWidth || 0;
      const n = Math.max(1, Math.min(4, Math.floor((w + MASONRY_GAP) / (MASONRY_MIN + MASONRY_GAP))));
      setCols((prev) => (prev === n ? prev : n));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [loaded]);

  const Container = embedded ? 'section' : 'main';
  const clientLabel = (p: ProjectListItem): string => {
    const c = clients?.find((x) => x.id === p.client_id);
    return (c && c.name) || p.client_name || (c && c.code) || p.client_code || 'Client';
  };

  // Client-side filter: name or code («W26083», «LP26083»).
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter((p) => {
      const code = p.number ? String(p.number) : '';
      return p.name.toLowerCase().includes(q) || code.includes(q.replace(/^(w|ts|lp)/i, ''));
    });
  }, [projects, query]);

  const createForm = (
    <form className="arch-create-form" onSubmit={create}>
      <div className="arch-create-field">
        <label htmlFor="arch-new-project-name">Start a new project</label>
        <input
          id="arch-new-project-name"
          ref={nameRef}
          type="text"
          placeholder="Project name"
          aria-label="New project name"
          maxLength={120}
        />
      </div>
      <button type="submit" className="arch-btn arch-btn-primary">
        <Plus size={15} aria-hidden="true" />
        Create project
      </button>
    </form>
  );

  return (
    <Container className={'arch-projects-main' + (embedded ? ' arch-projects-embedded' : '')}>
      {!embedded || !headerHasTitle ? <div className={'arch-projects-head' + (embedded ? ' is-compact' : '')}>
        <div className="arch-projects-intro">
          {!embedded ? <div className="arch-projects-eyebrow">Architecture workspace</div> : null}
          <div className="arch-projects-title">
            <h1>Projects</h1>
            {loaded && !embedded ? (
              <span className="arch-projects-count" aria-label={projects.length + ' projects'}>
                {projects.length}
              </span>
            ) : null}
          </div>
          {!embedded ? <p className="arch-projects-subtitle">Your architecture, documents and approvals in one place.</p> : null}
        </div>
        {!embedded ? createForm : null}
      </div> : null}
      <div className="arch-projects-tools">
          <div className="arch-projects-list-heading">
            <Folder size={16} strokeWidth={1.7} aria-hidden="true" />
            <span>All projects</span>
            {loaded && embedded ? <span className="arch-projects-count" aria-label={projects.length + ' projects'}>{projects.length}</span> : null}
            {loaded && query.trim() ? <span className="arch-projects-filter-count" role="status">{shown.length} results</span> : null}
          </div>
          <div className="arch-projects-tool-actions">
          <label className="arch-search">
            <Search className="arch-projects-search-icon" size={15} aria-hidden="true" />
            <input
              type="search"
              value={query}
              placeholder="Search by name or code"
              aria-label="Search projects"
              onChange={(e) => setQuery(e.target.value)}
            />
          </label>
          {embedded ? (
            <details
              ref={createMenuRef}
              className="arch-project-create-menu"
              onToggle={(e) => { if (e.currentTarget.open) nameRef.current?.focus(); }}
              onKeyDown={(e) => {
                if (e.key !== 'Escape' || !e.currentTarget.open) return;
                e.stopPropagation();
                e.currentTarget.open = false;
                e.currentTarget.querySelector('summary')?.focus();
              }}
            >
              <summary className="arch-btn arch-btn-primary">
                <Plus size={15} aria-hidden="true" />
                <span>New project</span>
                <ChevronDown size={15} aria-hidden="true" />
              </summary>
              <div className="arch-project-create-popover">{createForm}</div>
            </details>
          ) : null}
          </div>
      </div>
      {!loaded ? (
        <div className="arch-projects-grid" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <div className="arch-project-card arch-card-skel" key={i} style={{ ['--i' as string]: i } as React.CSSProperties}>
              <span className="arch-skel arch-skel-title" />
              <span className="arch-skel arch-skel-chip" />
              <span className="arch-skel arch-skel-line" />
              <span className="arch-skel arch-skel-strip" />
            </div>
          ))}
        </div>
      ) : null}
      <div
        className="arch-projects-masonry"
        ref={masonryRef}
        style={{ ['--cols' as string]: cols } as React.CSSProperties}
      >
        {Array.from({ length: cols }, (_, c) => (
          <div className="arch-masonry-col" key={c}>
        {shown.map((p, i) => ({ p, i })).filter(({ i }) => i % cols === c).map(({ p, i }) => {
          const generating = isGenerating(p);
          const open = () => {
            // Never open the project while its own card is being renamed.
            if (editingId === p.id) return;
            onOpen(p.id);
          };
          return (
            <div
              className={'arch-project-card' + (generating ? ' generating' : '')}
              key={p.id}
              style={{ ['--i' as string]: Math.min(i, 11) } as React.CSSProperties}
              role="button"
              tabIndex={0}
              onClick={open}
              onKeyDown={(e) => {
                if (e.target !== e.currentTarget) return;   // inputs/buttons inside keep their keys
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  open();
                }
              }}
            >
              <div className="arch-project-card-head">
                <span className="arch-project-card-icon" aria-hidden="true"><FolderOpen size={19} strokeWidth={1.65} /></span>
                <div className="arch-project-card-heading">
                  <InlineRename
                    variant="card"
                    value={p.name}
                    onSubmit={(nm) => renameCard(p, nm)}
                    onEditingChange={(ed) =>
                      // Only this card's own id clears the guard — a sibling card
                      // mounting (a poll adding a project) must not drop an edit in
                      // progress elsewhere.
                      setEditingId((prev) => (ed ? p.id : prev === p.id ? null : prev))
                    }
                  />
                  <ProjectCode
                    variant="card"
                    number={p.number}
                    docKind={p.doc_kind}
                    pipeline={p.pipeline}
                    onSave={(patch) => saveCardCode(p, patch)}
                  />
                </div>
              </div>
              <div className="arch-project-card-meta">
                <span><Clock3 size={12} aria-hidden="true" />{'Updated ' + fmtDate(p.updated_at)}</span>
                <span><History size={12} aria-hidden="true" />{(p.version_count || 0) + (p.version_count === 1 ? ' version' : ' versions')}</span>
              </div>
              <PipelineStrip pipeline={p.pipeline} className="arch-pipeline-card" />
              {overview?.projects[p.id] ? (
                <ApproversDetail
                  d={overview.projects[p.id]}
                  open={expanded.has(p.id)}
                  onToggle={() => toggleExpanded(p.id)}
                />
              ) : null}
              <div className="arch-project-card-foot">
                {generating ? (
                  <span className="arch-gen-chip" title="A generation is running">
                    <span className="arch-spinner arch-gen-spinner" />
                    Generating…
                  </span>
                ) : (
                  <span className="arch-card-open" aria-hidden="true">Open project <ArrowRight className="arch-card-open-arrow" size={14} /></span>
                )}
                {p.client_id ? (
                  /* linked: the client it belongs to, as a chip (a button when
                     the workspace can open that client) */
                  onOpenClient ? (
                    <button type="button" className="arch-card-client"
                      onClick={(e) => { e.stopPropagation(); onOpenClient(p.client_id as string); }}
                      title="Open client" aria-label={'Open client ' + clientLabel(p)}>
                      <Building2 size={12} aria-hidden="true" />
                      <span>{clientLabel(p)}</span>
                    </button>
                  ) : (
                    <span className="arch-card-client" title="Client">
                      <Building2 size={12} aria-hidden="true" />
                      <span>{clientLabel(p)}</span>
                    </span>
                  )
                ) : onLink ? (
                  <button type="button" className="arch-btn arch-btn-outline arch-btn-sm"
                    onClick={(e) => { e.stopPropagation(); onLink(p); }}
                    aria-label={'Link ' + p.name + ' to a client'}>
                    <FolderInput size={13} aria-hidden="true" /> Link
                  </button>
                ) : null}
                <button
                  className="arch-btn arch-btn-danger-ghost arch-btn-sm arch-card-del"
                  onClick={(e) => del(p, e)}
                  aria-label={'Delete ' + p.name}
                >
                  <Trash2 size={13} aria-hidden="true" />
                  <span>Delete</span>
                </button>
              </div>
            </div>
          );
        })}
          </div>
        ))}
      </div>
      {loaded && !projects.length ? (
        <div className="arch-empty arch-empty-projects">
          <div className="arch-empty-art" aria-hidden="true"><FolderPlus size={25} strokeWidth={1.6} /></div>
          <div className="arch-empty-title">No projects yet</div>
          <div className="arch-empty-sub">{embedded ? 'Choose New project to begin.' : 'Start with a project name above.'} Your architecture, estimate and proposal will live here.</div>
        </div>
      ) : null}
      {loaded && projects.length > 0 && !shown.length ? (
        <div className="arch-empty arch-empty-projects">
          <div className="arch-empty-art" aria-hidden="true"><Search size={24} strokeWidth={1.6} /></div>
          <div className="arch-empty-title">Nothing matches “{query.trim()}”</div>
          <button type="button" className="arch-btn arch-btn-outline arch-btn-sm" onClick={() => setQuery('')}>Clear search</button>
        </div>
      ) : null}
    </Container>
  );
}
